import React from "react";

/**
 * FileTypeIcon
 * A resizable SVG icon that shows a document with a folded corner
 * and the given file extension in large, readable letters.
 *
 * Props:
 *  - ext: string (e.g. "pdf", "jpg", "html"). Only the first 4 chars are shown.
 *  - size?: number | string - CSS size for width/height (default 112)
 *  - color?: string - base color (default is a blue-ish tone)
 *  - className?: string - optional className for custom styling
 */
export default function FileTypeIcon({
  ext,
  size = 112,
  color = "#3B82F6", // Tailwind's blue-500
  className,
}) {
  const text = (ext || "file").toUpperCase();
  const display = text.slice(0, 4);
  const len = display.length;
  // Adjust font-size to keep text readable regardless of length
  const fontSize = len <= 2 ? 48 : len === 3 ? 42 : 36;

  // Create two tints from the base color for subtle depth
  const lighten = (hex, amt) => {
    const c = hex.replace("#", "");
    const n = parseInt(c, 16);
    const r = Math.min(255, Math.max(0, (n >> 16) + amt));
    const g = Math.min(255, Math.max(0, ((n >> 8) & 0xff) + amt));
    const b = Math.min(255, Math.max(0, (n & 0xff) + amt));
    return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, "0")}`;
  };

  const base = color;
  const light = lighten(color, 24);
  const lighter = lighten(color, 255);

  return (
    <div
      className={className}
      style={{ width: size, height: size, display: "inline-block" }}
      aria-label={`${display} file icon`}
      role="img"
    >
      <svg
        viewBox="0 0 128 160"
        width="100%"
        height="100%"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Shadow */}
        <rect x="8" y="10" width="112" height="144" rx="12" fill="#000" opacity="0.06" />
        {/* Document body */}
        <rect x="8" y="6" width="112" height="144" rx="12" fill={base} />
        {/* Folded corner */}
        <path d="M88 6 L120 6 L120 38 Z" fill={lighter} />
        {/* Subtle fold edge */}
        <path d="M88 6 L120 38 L88 38 Z" fill={light} />

        {/* Extension text */}
        <g>
          <text
            x="64"
            y="118"
            textAnchor="middle"
            fontFamily="Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial"
            fontWeight={800}
            fontSize={fontSize}
            fill="#ffffff"
            letterSpacing="2"
          >
            {display}
          </text>
        </g>

      </svg>
    </div>
  );
}

// Example usage (remove or adapt in your app):
// <FileTypeIcon ext="docx" />
// <FileTypeIcon ext="jpg" size={72} />
// <FileTypeIcon ext="html" size="8rem" color="#2563EB" />
